import { useEffect, useMemo, useRef, useState, useCallback } from 'react'
import type { RefObject } from 'react'
import { useNavigate } from 'react-router-dom'
import { useOrg } from '@/stores/org'
import { useOrgQuery } from '@/lib/queries'
import { modelsKeys, fetchModels } from '@/lib/resources/models'
import { deploymentsKeys, fetchDeployments } from '@/lib/resources/deployments'
import { trainingKeys, fetchTrainingJobs } from '@/lib/resources/training'
import { shortId } from '@/lib/unwrap'
import { useTranslation } from 'react-i18next'
import { NAV_ITEMS } from '@/lib/nav'
import { Search, Loader2, ArrowRight, Box, Rocket, GraduationCap } from 'lucide-react'
import { Modal } from '@/components/ui/modal'

type CommandItem = {
  id: string
  label: string
  sub: string
  to: string
  kind: 'route' | 'model' | 'deployment' | 'training'
  keywords: string
  hint?: string
  // label falls back to a short ID when the entity has no name — keep identity in mono
  monoLabel?: boolean
}

function matches(query: string, hay: string): boolean {
  const q = query.trim().toLowerCase()
  if (!q) return true
  const h = hay.toLowerCase()
  if (h.includes(q)) return true
  // fuzzy: chars in order
  let hi = 0
  for (const ch of q) {
    const idx = h.indexOf(ch, hi)
    if (idx === -1) return false
    hi = idx + 1
  }
  return true
}

const ENTITY_LIMIT = 6
const IS_MAC = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform)
const SHORTCUT_LABEL = IS_MAC ? '⌘K' : 'Ctrl+K'

export function CommandPalette() {
  const { t } = useTranslation()
  /** A type assertion, not a wrapper — `tr === t` at runtime. react-i18next
   *  rebinds `t` on `languageChanged`, so a memo that depends on `tr` follows
   *  the language. */
  const tr = t as unknown as (k: string, o?: Record<string, unknown>) => string
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [activeIdx, setActiveIdx] = useState(0)
  const inputRef = useRef<HTMLInputElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const mobileTriggerRef = useRef<HTMLButtonElement>(null)
  const listRef = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()
  const { activeOrgId } = useOrg()

  const enabled = open && !!activeOrgId

  const modelsQ = useOrgQuery(
    modelsKeys.palette,
    (orgId) => fetchModels(orgId, { limit: String(ENTITY_LIMIT * 2) }),
    { enabled: open, staleTime: 15_000 },
  )

  const deploymentsQ = useOrgQuery(
    deploymentsKeys.palette,
    (orgId) => fetchDeployments(orgId, { limit: String(ENTITY_LIMIT * 2) }),
    { enabled: open, staleTime: 15_000 },
  )

  const trainingQ = useOrgQuery(
    trainingKeys.palette,
    (orgId) => fetchTrainingJobs(orgId, { limit: String(ENTITY_LIMIT * 2) }),
    { enabled: open, staleTime: 15_000 },
  )

  const isLoading = enabled && (modelsQ.isLoading || deploymentsQ.isLoading || trainingQ.isLoading)

  const routeItems: CommandItem[] = useMemo(
    () =>
      NAV_ITEMS.map((n) => ({
        id: `route:${n.to}`,
        label: tr(n.labelKey),
        sub: tr(n.subKey),
        to: n.to,
        kind: 'route' as const,
        keywords: n.keywords ?? '',
        hint: n.to,
      })),
    // `tr` IS `t` at runtime (a type assertion, not a wrapper), and react-i18next
    // swaps `t` for a new one on `languageChanged` — so `[tr]` is what makes the
    // labels follow the language. `i18n.language` was redundant on top of it.
    [tr],
  )

  const entityItems: CommandItem[] = useMemo(() => {
    const out: CommandItem[] = []
    const models = modelsQ.data?.models ?? []
    for (const m of models.slice(0, ENTITY_LIMIT)) {
      out.push({
        id: `model:${m.id}`,
        label: m.name || shortId(m.id),
        sub: t('commandPalette.entityModel'),
        to: `/models/${m.id}`,
        kind: 'model',
        keywords: `${m.name} ${m.id} moxing`,
        hint: shortId(m.id),
        monoLabel: !m.name,
      })
    }
    const deployments = deploymentsQ.data?.deployments ?? []
    for (const d of deployments.slice(0, ENTITY_LIMIT)) {
      out.push({
        id: `dep:${d.id}`,
        label: d.name || shortId(d.id),
        sub: t('commandPalette.entityDeployment'),
        to: `/deployments/${d.id}`,
        kind: 'deployment',
        // ADR-0052 / #47: a deployment row names no machine any more — the
        // palette searches the identity and the promise, not the card behind it.
        keywords: `${d.name} ${d.id} bushu`,
        hint: `${d.state ?? ''} ${shortId(d.id)}`.trim(),
        monoLabel: !d.name,
      })
    }
    const jobs = trainingQ.data?.jobs ?? []
    for (const j of jobs.slice(0, ENTITY_LIMIT)) {
      out.push({
        id: `train:${j.id}`,
        label: shortId(j.id),
        sub: t('commandPalette.entityTraining'),
        to: `/training/${j.id}`,
        kind: 'training',
        keywords: `${j.id} ${j.method ?? ''} ${j.state ?? ''} xunlian`,
        hint: `${j.method ?? ''} ${j.state ?? ''}`.trim(),
        monoLabel: true,
      })
    }
    return out
    // `t` is a new function on every `languageChanged` (react-i18next rebinds it),
    // so the labels follow the language through `t` alone — `i18n.language` was
    // a second, redundant trigger for the same recompute.
  }, [modelsQ.data, deploymentsQ.data, trainingQ.data, t])

  const allItems = useMemo(() => [...routeItems, ...entityItems], [routeItems, entityItems])

  const filtered = useMemo(() => {
    if (!query.trim()) return allItems
    return allItems.filter((it) => matches(query, [it.label, it.sub, it.keywords, it.hint ?? ''].join(' ')))
  }, [allItems, query])

  // clamp activeIdx — the highlight never points past the filtered list.
  // Adjusting during render keeps the clamp in the same commit as the list
  // change, instead of a render where the highlight is briefly out of range.
  if (activeIdx >= filtered.length) setActiveIdx(0)

  useEffect(() => {
    // keep focused option visible
    if (!listRef.current) return
    const el = listRef.current.querySelector<HTMLElement>(`[data-idx="${activeIdx}"]`)
    el?.scrollIntoView({ block: 'nearest' })
  }, [activeIdx])

  // Modal restores focus to the element focused at open; this ref is only its
  // fallback when that element is gone. It must resolve to whichever trigger is
  // actually visible at close time (sm+ pill vs <sm icon), so it's a getter
  // over both refs — Modal only ever reads `.current`, once, at cleanup.
  const visibleTriggerRef = useMemo<RefObject<HTMLElement | null>>(
    () => ({
      get current() {
        for (const el of [triggerRef.current, mobileTriggerRef.current]) {
          if (el?.isConnected && (typeof el.checkVisibility !== 'function' || el.checkVisibility())) {
            return el
          }
        }
        return null
      },
    }),
    [],
  )

  const close = useCallback(() => {
    setOpen(false)
  }, [])

  const openPalette = useCallback(() => {
    setQuery('')
    setActiveIdx(0)
    setOpen(true)
  }, [])

  // global Cmd/Ctrl+K
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      const isK = e.key.toLowerCase() === 'k'
      if ((e.metaKey || e.ctrlKey) && isK) {
        e.preventDefault()
        if (open) close()
        else openPalette()
      } else if (e.key === 'Escape' && open) {
        e.preventDefault()
        close()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, close, openPalette])

  // focus (initialFocusRef → input) + scroll lock + focus restore all live in ui/Modal

  const doNavigate = useCallback(
    (item: CommandItem) => {
      close()
      navigate(item.to)
    },
    [close, navigate],
  )

  const onInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setActiveIdx((i) => (filtered.length ? (i + 1) % filtered.length : 0))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setActiveIdx((i) => (filtered.length ? (i - 1 + filtered.length) % filtered.length : 0))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      const cur = filtered[activeIdx]
      if (cur) doNavigate(cur)
    } else if (e.key === 'Escape') {
      e.preventDefault()
      close()
    }
  }

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={openPalette}
        aria-label={t('commandPalette.open')}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-keyshortcuts="Meta+K Control+K"
        className="hidden h-9 items-center gap-2 rounded-full border border-enterprise-border bg-enterprise-surface px-3 text-sm text-enterprise-muted transition-colors hover:bg-enterprise-raised hover:text-enterprise-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-enterprise-indigo focus-visible:ring-offset-2 focus-visible:ring-offset-enterprise-bg sm:inline-flex lg:inline-flex"
      >
        <Search className="h-4 w-4" aria-hidden="true" />
        <span className="hidden 2xl:inline">{t('common.search')}</span>
        <span className="hidden items-center gap-1 rounded-none border border-enterprise-border bg-enterprise-raised px-1.5 py-0.5 font-mono text-xs text-enterprise-muted 2xl:inline-flex">
          {SHORTCUT_LABEL}
        </span>
      </button>
      {/* mobile icon trigger — keeps palette reachable without keyboard */}
      <button
        ref={mobileTriggerRef}
        type="button"
        onClick={openPalette}
        aria-label={t('commandPalette.open')}
        aria-haspopup="dialog"
        aria-expanded={open}
        className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-enterprise-border bg-enterprise-surface text-enterprise-muted transition-colors hover:bg-enterprise-raised hover:text-enterprise-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-enterprise-indigo focus-visible:ring-offset-2 focus-visible:ring-offset-enterprise-bg sm:hidden"
      >
        <Search className="h-4 w-4" aria-hidden="true" />
      </button>

      <Modal
        open={open}
        onClose={close}
        label={t('commandPalette.title')}
        backdropLabel={t('commandPalette.close')}
        overlayClassName="items-start justify-center p-4 pt-[10vh] sm:pt-[14vh]"
        backdropClassName="backdrop-blur-sm"
        initialFocusRef={inputRef}
        restoreFocusRef={visibleTriggerRef}
        className="palette-panel flex max-h-[min(72vh,560px)] w-full max-w-[640px] flex-col overflow-hidden rounded-none border border-enterprise-border bg-enterprise-surface shadow-none"
      >
            {/* search input row */}
            <div className="flex items-center gap-3 border-b border-enterprise-border px-4 py-3">
              <Search className="h-5 w-5 shrink-0 text-enterprise-muted" aria-hidden="true" />
              <input
                ref={inputRef}
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value)
                  setActiveIdx(0)
                }}
                onKeyDown={onInputKeyDown}
                placeholder={t('commandPalette.placeholder')}
                role="combobox"
                aria-expanded={true}
                aria-autocomplete="list"
                aria-haspopup="listbox"
                aria-label={t('commandPalette.searchLabel')}
                aria-controls="palette-listbox"
                aria-activedescendant={filtered[activeIdx] ? `palette-opt-${filtered[activeIdx]!.id}` : undefined}
                className="h-8 w-full bg-transparent font-mono text-sm text-enterprise-text placeholder:text-enterprise-subtle focus:outline-none"
                autoComplete="off"
                autoCorrect="off"
                spellCheck={false}
              />
              <span className="hidden shrink-0 rounded-none border border-enterprise-border bg-enterprise-raised px-1.5 py-0.5 font-mono text-xs text-enterprise-muted sm:inline-flex">
                ESC
              </span>
            </div>

            {/* list */}
            <div ref={listRef} className="overflow-y-auto p-2" role="listbox" id="palette-listbox" aria-label={t('commandPalette.searchResults')}>
              {isLoading ? (
                <div className="flex items-center justify-center gap-2 px-4 py-10 text-sm text-enterprise-muted" role="status" aria-live="polite" aria-busy="true">
                  <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                  <span>{t('commandPalette.loading')}</span>
                </div>
              ) : filtered.length === 0 ? (
                <div className="px-4 py-10 text-center" role="status" aria-live="polite">
                  <div className="mx-auto flex h-10 w-10 items-center justify-center rounded-none border border-dashed border-enterprise-border bg-enterprise-bg text-enterprise-muted">
                    <Search className="h-5 w-5" aria-hidden="true" />
                  </div>
                  <p className="mt-3 text-section text-enterprise-text">{t('commandPalette.noResults')}</p>
                  <p className="mt-1 text-xs text-enterprise-muted">{t('commandPalette.noResultsHint')}</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* routes group */}
                  {(() => {
                    const routes = filtered.filter((f) => f.kind === 'route')
                    const entities = filtered.filter((f) => f.kind !== 'route')
                    const idxById = new Map<string, number>()
                    filtered.forEach((f, i) => idxById.set(f.id, i))
                    return (
                      <>
                        {routes.length ? (
                          <div>
                            <div className="px-2 pb-1 pt-1 font-mono text-xs tracking-wide text-enterprise-muted">{t('commandPalette.groupPages')}</div>
                            <div className="space-y-1">
                              {routes.map((it) => {
                                const gIdx = idxById.get(it.id) ?? -1
                                const active = gIdx === activeIdx
                                return (
                                  <button
                                    key={it.id}
                                    id={`palette-opt-${it.id}`}
                                    data-idx={gIdx}
                                    role="option"
                                    aria-selected={active}
                                    onMouseEnter={() => setActiveIdx(gIdx)}
                                    onClick={() => doNavigate(it)}
                                    className={[
                                      'flex w-full h-9 items-center justify-between rounded-full px-4 text-left text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-enterprise-indigo focus-visible:ring-offset-2 focus-visible:ring-offset-enterprise-bg',
                                      active ? 'bg-enterprise-indigo text-white' : 'text-enterprise-text hover:bg-enterprise-raised',
                                    ].join(' ')}
                                  >
                                    <span className="flex items-center gap-2.5">
                                      <span className={['inline-flex h-7 w-7 items-center justify-center rounded-none border text-xs', active ? 'border-white/20 bg-white/15 text-white' : 'border-enterprise-border bg-enterprise-raised text-enterprise-muted'].join(' ')}>
                                        <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
                                      </span>
                                      <span className="font-medium tracking-tight">{it.label}</span>
                                      <span className={['text-xs tracking-wide', active ? 'text-white/80' : 'text-enterprise-muted'].join(' ')}>{it.sub}</span>
                                    </span>
                                    <span className={['hidden font-mono text-xs sm:inline', active ? 'text-white/70' : 'text-enterprise-muted'].join(' ')}>{it.hint}</span>
                                  </button>
                                )
                              })}
                            </div>
                          </div>
                        ) : null}

                        {entities.length ? (
                          <div>
                            <div className="px-2 pb-1 pt-1 font-mono text-xs tracking-wide text-enterprise-muted">{t('commandPalette.groupEntities')}</div>
                            <div className="space-y-1">
                              {entities.map((it) => {
                                const gIdx = idxById.get(it.id) ?? -1
                                const active = gIdx === activeIdx
                                const Icon = it.kind === 'model' ? Box : it.kind === 'deployment' ? Rocket : GraduationCap
                                return (
                                  <button
                                    key={it.id}
                                    id={`palette-opt-${it.id}`}
                                    data-idx={gIdx}
                                    role="option"
                                    aria-selected={active}
                                    onMouseEnter={() => setActiveIdx(gIdx)}
                                    onClick={() => doNavigate(it)}
                                    className={[
                                      'flex w-full h-9 items-center justify-between gap-3 rounded-full px-4 text-left text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-enterprise-indigo focus-visible:ring-offset-2 focus-visible:ring-offset-enterprise-bg',
                                      active ? 'bg-enterprise-indigo text-white' : 'text-enterprise-text hover:bg-enterprise-raised',
                                    ].join(' ')}
                                  >
                                    <span className="flex min-w-0 items-center gap-2.5">
                                      <span className={['inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-none border', active ? 'border-white/20 bg-white/15 text-white' : 'border-enterprise-border bg-enterprise-raised text-enterprise-muted'].join(' ')}>
                                        <Icon className="h-3.5 w-3.5" aria-hidden="true" />
                                      </span>
                                      <span className="min-w-0">
                                        <span className={['block truncate font-medium tracking-tight', it.monoLabel ? 'font-mono' : ''].join(' ')}>{it.label}</span>
                                        <span className={['block truncate font-mono text-xs', active ? 'text-white/80' : 'text-enterprise-muted'].join(' ')}>{it.hint || it.sub}</span>
                                      </span>
                                    </span>
                                    <span className={['shrink-0 rounded-full border px-2 py-0.5 text-xs', active ? 'border-white/20 bg-white/15 text-white' : 'border-enterprise-border bg-enterprise-raised text-enterprise-muted'].join(' ')}>
                                      {it.sub}
                                    </span>
                                  </button>
                                )
                              })}
                            </div>
                          </div>
                        ) : null}
                      </>
                    )
                  })()}
                </div>
              )}
            </div>

            {/* footer hint */}
            <div className="flex items-center justify-between border-t border-enterprise-border bg-enterprise-raised px-3 py-2 font-mono text-xs text-enterprise-muted">
              <span className="hidden sm:inline">{t('commandPalette.footer')}</span>
              <span className="sm:hidden">{t('commandPalette.footerMobile')}</span>
              <span className="hidden sm:inline">{t('commandPalette.shortcutHint')}</span>
            </div>
      </Modal>


      <style>{`@media (prefers-reduced-motion: no-preference) {
  .palette-panel { animation: paletteIn var(--motion-duration-fast) var(--motion-ease-enter); }
  @keyframes paletteIn { from { opacity: 0; transform: translateY(4px) scale(0.99); } to { opacity: 1; transform: translateY(0) scale(1); } }
}`}</style>
    </>
  )
}
